<template>
  <b-dropdown
    :id="`${data.field.key}-filter-arrow`"
    :ref="'columnOptionsDropdown_'+data.field.key"
    menu-class="filter-dropdown-menu"
    variant="link"
    class="header-actions filter-dropdown"
    @shown="onHeaderDropdownShown(data)"
    @hidden="onHeaderDropdownHidden()"
  >
    <b-dropdown-form @submit.prevent="onFilter(data)">
      <b-form-group
        v-if="!isSelectColumn(data.field) && !isLoanCalculatorOutput"
        class="mb-0"
      >
        <b-dropdown-item
          :id="`${data.field.key}-filter-ascending`"
          @click="sortChanged(data.field, false)"
        >
          <font-awesome-icon :icon="['fas', 'sort-amount-up-alt']" />
          Sort Ascending
        </b-dropdown-item>
        <b-dropdown-item
          :id="`${data.field.key}-filter-descending`"
          @click="sortChanged(data.field, true)"
        >
          <font-awesome-icon :icon="['fas', 'sort-amount-down']" />
          Sort Descending
        </b-dropdown-item>
        <b-dropdown-item
          :id="`${data.field.key}-filter-clear`"
          @click="sortChanged(data.field, null)"
        >
          <font-awesome-icon :icon="['fas', 'times']" />
          Clear Sort
        </b-dropdown-item>
        <b-dropdown-divider />
      </b-form-group>
      <b-form-group class="mb-0">
        <b-dropdown-item
          v-if="!isSelectColumn(data.field)"
          :id="`${data.field.key}-group-by`"
          :disabled="groupBy && subGroupBy && data.field.key !== groupBy && data.field.key !== subGroupBy"
          class="flex align-items-center"
          @click="onGroupBy(data.field)"
        >
          <font-awesome-icon
            :icon="['fas', 'layer-group']"
            class="ml-2"
          />
          <div class="mr-2">
            {{ isGrouped ? 'Remove Grouping' : 'Group By' }}
          </div>
        </b-dropdown-item>
        <b-dropdown-item
          v-if="!isFieldRecordStatus(data) && !isSelectColumn(data.field)"
          :id="`${data.field.key}-freeze-column`"
          class="flex align-items-center"
          @click="onFreeze(data.field)"
        >
          <font-awesome-icon
            :icon="['fas', 'lock']"
            class="ml-2"
          />
          <div class="mr-2">
            {{ isSticky ? 'Unfreeze column' : 'Freeze column' }}
          </div>
        </b-dropdown-item>
        <b-dropdown-divider v-if="!isSelectColumn(data.field)" />
      </b-form-group>
      <template
        v-for="filterOption in filterOptions"
        v-if="
          !isUserField(data.field)
            && !isFieldHierarchy(data.field)
            && !isDisabledOption(filterOption,data)
            && !isFieldRecordStatus(data)
            && !isSelectColumn(data.field)
            && !isFieldPostedStatus(data.field)
        "
      >
        <b-form-group class="mb-0 filter-option-group">
          <b-form-radio-group
            :name="`radios-stacked-${data.field.key}`"
            v-model="filterType.value"
            stacked
            :disabled="isDisabledOption(filterOption, data)"
            @change="focusOnInput($event, data.field.key)"
          >
            <b-form-radio
              v-for="option in filterOption"
              :key="option.value"
              :id="`${data.field.key}-filter-${option.id}`"
              :value="option.value"
            >
              {{ option.text }}
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
        <b-dropdown-divider />
      </template>
      <b-form-group
        label-for="dropdown-form-email"
        @submit.stop.prevent
      >
        <b-form-input
          v-if="
            !isUserField(data.field)
              && !isFieldDateTime(data.field)
              && !isFieldDate(data.field)
              && !isFieldBoolean(data.field)
              && !isFieldLookup(data.field)
              && !isFieldHierarchy(data.field)
              && !isFieldRecordStatus(data)
              && !isSelectColumn(data.field)
              && !isFieldPostedStatus(data.field)
          "
          :id="`${data.field.key}-filter-input`"
          :ref="'filterInput-'+data.field.key"
          v-model="filterText.value"
          class="filter-search-text"
          :placeholder="isFieldNumeric(data.field) ? 'Filter Value' : 'Filter Text'"
          :state="isInvalidInput ? false : null" 
        />
        <b-form-invalid-feedback v-if="isInvalidInput">
          Enter a valid number to use this filter.
        </b-form-invalid-feedback>
        <template v-if="isFieldDateTime(data.field) || isFieldDate(data.field)">
          <b-form-group>
            <label>From Date</label>
            <div>
              <custom-date-picker
                v-model="fromDate.displayValue"
                input-class="input-editor form-control"
                class="editor-w from-date-input-wrapper"
                :type="isFieldDateTime(data.field) ? 'datetime' : 'date'"
                :format="getFieldDateFormat()"
                :open="fromDate.open"
                :placeholder="getFieldDateFormat()"
                :value-type="getFieldDateFormat()"
                :formatter="momentFormat"
                :input-class="`form-control ${!fromDate.validation.isValid ? 'datepicker-validation' : '' }`"
                :disabled-date="fromDateDisabledDates"
                @pick="onDatePick($event, 'fromDate')"
                @close="onDatePickerClose('fromDate')"
                @clear="onDatePickerClear('fromDate')"
                @open="onDatePickerOpen('fromDate')"
                @keydown.native="onDatePickerKeyDown($event, 'fromDate')"
              />
            </div>
            <b-form-invalid-feedback :state="fromDate.validation.isValid">
              {{ fromDate.validation.errorMessage }}
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group>
            <label>To Date</label>
            <div>
              <custom-date-picker
                v-model="toDate.displayValue"
                input-class="input-editor form-control"
                class="editor-w to-date-input-wrapper"
                :type="isFieldDateTime(data.field) ? 'datetime' : 'date'"
                :format="getFieldDateFormat()"
                :open="toDate.open"
                :placeholder="getFieldDateFormat()"
                :value-type="getFieldDateFormat()"
                :input-class="`form-control ${!toDate.validation.isValid ? 'datepicker-validation' : '' }`"
                :disabled-date="toDateDisabledDates"
                :formatter="momentFormat"
                @pick="onDatePick($event, 'toDate')"
                @close="onDatePickerClose('toDate')"
                @clear="onDatePickerClear('toDate')"
                @open="onDatePickerOpen('toDate')"
                @keydown.native="onDatePickerKeyDown($event, 'toDate')"
              />
            </div>
            <b-form-invalid-feedback :state="toDate.validation.isValid">
              {{ toDate.validation.errorMessage }}
            </b-form-invalid-feedback>
          </b-form-group>
        </template>

        <template v-if="isSelectColumn(data.field)">
          <b-form-group>
            <label>Created Date</label>
            <b-form-input
              :ref="`date-${data.field.key}`"
              v-model="createdDate"
              type="date"
              class="input-editor"
              placeholder="Created Date"
            />
          </b-form-group>
          <b-form-group>
            <label>Updated Date</label>
            <b-form-input
              v-model="updatedDate"
              type="date"
              class="input-editor"
              placeholder="Updated Date"
            />
          </b-form-group>
        </template>
        <div
          v-if="
            isFieldLookup(data.field)
              || isFieldHierarchy(data.field)
              || isUserField(data.field)
          "
          class="lookup-filter"
        >
          <span class="spinner">
            <b-spinner 
              v-if="lookupLoadingState"          
              small 
            />         
          </span>
           
          <lookup-input-component
            v-if="showMultiselect"
            :class="lookupLoadingState ? 'hide-lookup' : ''"
            :field="data.field"
            :is-filter-dropdown="true"
            :index="1"
            :props-value="selectItems"
            transit-ref="lkp-multiselect"
            @on-select="chooseItem"
            @on-remove="chooseItem"
            @loading-end="lookupLoadingState = false"
          />   
        </div>
        <b-form-select
          v-if="isFieldBoolean(data.field)"
          v-model="selectedOption"
        >
          <b-form-select-option value="-1">
            All
          </b-form-select-option>
          <b-form-select-option value="1">
            Checked
          </b-form-select-option>
          <b-form-select-option value="0">
            Unchecked
          </b-form-select-option>
        </b-form-select>

        <b-form-checkbox-group
          v-if="isFieldRecordStatus(data)"
          v-model="selectedStatus"
          :options="statusOptions"
          stacked
        />
        <b-form-checkbox-group
          v-if="isFieldPostedStatus(data.field)"
          v-model="selectedPostedStatuses"
          :options="postedStatusOptions"
          stacked
        />
      </b-form-group>
      <div class="d-flex justify-content-between">
        <span v-b-tooltip.hover.top="isFilterButtonDisabled() ? 'Enter a valid number to use this filter.' : ''">
          <b-button
            :id="`${data.field.key}-filter-submit`"
            type="submit"
            size="sm"
            variant="primary"
            class="filter-dropdown-filter-button"
            :disabled="isFilterButtonDisabled()"
          >
            <font-awesome-icon :icon="['fas', 'filter']" />
            Filter
          </b-button>
        </span>
        <b-button
          :id="`${data.field.key}-clear-filters`"
          size="sm"
          variant="secondary"
          class="filter-dropdown-clear-button"
          @click="clearFilters(data)"
        >
          <font-awesome-icon :icon="['fas', 'times']" />
          Clear filters
        </b-button>
      </div>
    </b-dropdown-form>
  </b-dropdown>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import moment, { invalid } from 'moment';
import _ from 'lodash';
import LookupInputComponent from '../../LookupInputComponent.vue';

const {mapState} = createNamespacedHelpers('app');
const {
  mapState: mapStateDataManagement,
  mapActions: mapActionsDataManagement,
} = createNamespacedHelpers('dataManagement');

export default {
  name: 'FilterDropdown',
  components: {
    LookupInputComponent,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    filters: {
      type: Object,
      required: true,
    },
    groupBy: {
      type: String,
      required: false,
    },
    subGroupBy: {
      type: String,
      required: false,
    },
    isLoanCalculatorOutput: {
      type: Boolean,
      default: false,
    },
    options: Array,
    selected: String,
    isGrouped: Boolean,
    isSticky: Boolean,
  },
  data() {
    return {
      momentFormat: {
        stringify: (date) => {
          return date ? moment(date, this.getFieldDateFormat(), true).format(this.getFieldDateFormat()) : '';
        },
        parse: (value) => {
          return value ? moment(value, this.getFieldDateFormat(), true).toDate() : null;
        },
      },
      filterSearch: '',
      selectItems: [],
      selectedOption: -1,
      filterType: {
        field: null,
        value: '',
      },
      filterText: {
        field: null,
        value: null,
      },
      fromDate: {
        value: null,
        displayValue: null,
        open: false,
        validation: {
          isValid: true,
          errorMessage: `Date format must be ${this.getFieldDateFormat()}`,
        },
      },
      toDate: {
        value: null,
        displayValue: null,
        open: false,
        validation: {
          isValid: true,
          errorMessage: `Date format must be ${this.getFieldDateFormat()}`,
        },
      },
      sortBy: null,
      createdDate: null,
      updatedDate: null,
      sortDesc: false,
      datePickerSubmit: false,
      statusOptions: [
        {text: 'Confirmed', value: 'confirmed'},
        {text: 'Validated', value: 'validated'},
        {text: 'Validation Failed', value: 'validationFailed'},
      ],
      postedStatusOptions: [
        {text: 'Posted', value: 'Posted'},
        {text: 'Pending', value: 'Pending'},
      ],
      selectedStatus: [],
      selectedPostedStatuses: [],
      saveSearchMapForDebounce: new Map(),
      foundOptions: [],
      showMultiselect: false,
      lookupLoadingState: true, //Necessary to improve the UI during loading state
    };
  },
  watch: {
    'fromDate.value'() {
      this.updateSecondDate(new Date(this.fromDate.value), 'fromDate');
    },
    'toDate.value'() {
      this.updateSecondDate(new Date(this.toDate.value), 'toDate');
    },
  },
  computed: {
    ...mapState([
      'filterOptions',
      'filterOptionsText',
    ]),
    ...mapStateDataManagement({
      fieldValues: state => state.fieldValues,
      isLookupFieldValuesLoading: state => state.pagedDataLookupValuesLoading,
    }),
    lookupOptions() {
      if (this.isFieldLookup(this.data.field)) {
        return this.filterSearch
          ? this.foundOptions
          : !this.filterSearch
            ? _.uniqBy(this.data.field.data?.options.concat(this.selectItems), 'id')
            : this.data.field.data?.options;
      } else if (this.isFieldHierarchy(this.data.field)) {
        const fieldName = this.data.field.data.options.hierarchyField.display.name;
        const uuidName = this.data.field.data.options.hierarchyField.primary.name;
        return this.data.field.data.options.items
          .filter((elem) => {
            const text = elem[fieldName] && typeof elem[fieldName] === 'string' ? elem[fieldName].toLowerCase() : elem[fieldName];
            const search = this.filterSearch && typeof this.filterSearch === 'string' ? this.filterSearch.toLowerCase() : this.filterSearch;
            if (text) {
              return typeof text === 'string' ? text.includes(search) : text.toString().includes(search);
            }
          })
          .sort(function (a, b) {
            const aText = a[fieldName] && typeof a[fieldName] === 'string' ? a[fieldName].toLowerCase() : a[fieldName];
            const bText = b[fieldName] && typeof b[fieldName] === 'string' ? b[fieldName].toLowerCase() : b[fieldName];
            return aText < bText ? -1 : aText > bText ? 1 : 0;
          }).map(a => {
            return {
              id: a[uuidName],
              text: a[fieldName],
            };
          });
      } else if (this.isUserField(this.data.field)) {
        return this.data.field.options.filter(elem => {
          const text = elem.text && typeof elem.text === 'string' ? elem.text.toLowerCase() : elem.text;
          const search = this.filterSearch && typeof this.filterSearch === 'string' ? this.filterSearch.toLowerCase() : this.filterSearch;
          if (text) {
            return typeof text === 'string' ? text.includes(search) : text.toString().includes(search);
          }
        });
      }
    },
    allItemsSelected() {
      return this.selectItems.length === this.lookupOptions.length;
    },
    lookupOptionExist() {
      return this.lookupOptions && !!this.lookupOptions.length;
    },
    isInvalidInput() {
      return this.filterText.value && this.isFilterButtonDisabled();
    },
  },
  methods: {
    ...mapActionsDataManagement(['getPagedDataLookupValueItems']),
    searchWithDebounce(field, query, debounceMS = 1000) {
      let timeout = this.saveSearchMapForDebounce.get(field.fieldGuid);

      if (timeout) {
        clearTimeout(timeout);
        this.saveSearchMapForDebounce.delete(field.fieldGuid);
      }

      timeout = setTimeout(async () => {
        await this.lookupOptionsFind(field, query);
        clearTimeout(timeout);
      }, debounceMS);

      this.saveSearchMapForDebounce.set(field.fieldGuid, timeout);
    },
    formatDecimal(number, decimalPlaces) {
      const numString = number.toString();

      const [integerPart, decimalPart] = numString.split('.');

      const formattedDecimalPart = decimalPart
        ? decimalPart.padEnd(decimalPlaces, '0')
        : '0'.repeat(decimalPlaces);

      return `${integerPart || '0'}.${formattedDecimalPart}`;
    },
    async lookupOptionsFind(field, query) {
      const {body} = await this.getPagedDataLookupValueItems({field, query});
      const mdmDataTypeJSON = JSON.parse(body.mdmDataTypeJSON);
      const dataTypeName = mdmDataTypeJSON.UnderlyingDataType.split('.')[1];

      let foundOptions;

      if (dataTypeName === 'Decimal') {
        const scale = mdmDataTypeJSON.Scale;
        foundOptions = [...body.data.map(val => ({
          id: val.recordGuid,
          text: this.formatDecimal(val.value, scale).split(',').join(''),
        }))];
      } else {
        foundOptions = [...body.data.map(val => ({id: val.recordGuid, text: val.value}))];
      }

      this.foundOptions = JSON.parse(JSON.stringify(foundOptions));
    },
    getFieldDateFormat() {
      const jsonData = this.data.field.dataTypeJSON ? JSON.parse(this.data.field.dataTypeJSON) : {};
      const dateFormat = jsonData.FormatString || 'YYYY-MM-DD';
      
      return `${dateFormat}${this.data.field.dataTypeName === 'dateTime' ? ' HH:mm:ss' : ''}`;
    },
    getFormattedDate(value, format = 'YYYY-MM-DD') {
      return moment(value).format(format);
    },
    onDatePick(value, filterName) {
      this.datePickerSubmit = true;
      this[filterName].value = this.getFormattedDate(value);
      this[filterName].displayValue = this.getFormattedDate(value, this.getFieldDateFormat());
      this.$refs[`columnOptionsDropdown_${this.data.field.key}`].show();
      this.onDatePickerKeyDown(null, filterName, true);
    },
    updateSecondDate(value, filterName) {
      const secondDate = new Date(value);
      if (filterName === 'fromDate' && !this.toDate.displayValue) {
        secondDate.setHours(23);
        secondDate.setMinutes(59);
        secondDate.setSeconds(59);
        this.toDate.displayValue = this.getFormattedDate(secondDate, this.getFieldDateFormat());
        this.toDate.value = this.getFormattedDate(secondDate, 'YYYY-MM-DD HH:mm:ss');
      } else if (filterName === 'toDate' && !this.fromDate.displayValue) {
        secondDate.setHours(0);
        secondDate.setMinutes(0);
        secondDate.setSeconds(0);
        this.fromDate.displayValue = this.getFormattedDate(secondDate, this.getFieldDateFormat());
        this.fromDate.value = this.getFormattedDate(secondDate, 'YYYY-MM-DD HH:mm:ss');
      }
    },
    onDatePickerKeyDown(e, filterName, isPick = false) {
      if (e && e.key === 'Enter' || isPick) {
        this[filterName].validation.isValid = !!this[filterName].displayValue;
      }
    },
    onDatePickerOpen(filterName) {
      this[filterName].open = true;
    },
    onDatePickerClose(filterName) {
      this[filterName].open = false;

      setTimeout(() => {
        if (!!this[filterName].displayValue) {
          this[filterName].validation.isValid = true;
          this[filterName].value = moment(this[filterName].displayValue, this.getFieldDateFormat()).format(`YYYY-MM-DD${this.isFieldDateTime(this.data.field) ? ' HH:mm:ss' : ''}`);
        } else {
          this[filterName].validation.isValid = false;
        }
      });
    },
    onDatePickerClear(filterName) {
      this[filterName] = {
        ...this[filterName],
        value: null,
        displayValue: null,
        open: false,
      };
      this[filterName].validation.isValid = true;
    },
    getName(item) {
      return item.text;
    },
    isActive(item) {
      if (this.isFieldLookup(this.data.field) || this.isFieldHierarchy(this.data.field)) {
        return this.selectItems.findIndex((elem) => elem.id === item.id) > -1;
      } else if (this.isUserField(this.data.field)) {
        return this.selectItems.findIndex((elem) => elem.value === item.value) > -1;
      }
    },
    chooseItem(item) {
      if (this.isFieldLookup(this.data.field) || this.isFieldHierarchy(this.data.field)) {
        const index = this.selectItems.findIndex((elem) => elem.id === item.id);
        if (index < 0) {
          this.selectItems.push(item);
        } else {
          this.selectItems.splice(index, 1);
        }
      } else if (this.isUserField(this.data.field)) {
        this.selectItems = [];

        this.selectItems.push(item);
      }
    },
    /**
     * This method is triggered when the header dropdown of a table column is shown. 
     * It handles UI interactions, updates filter values, and manages component states based on the field type.
     * @param data - contains information about the field for which the dropdown is being displayed.
     */
    onHeaderDropdownShown(data) {
      this.$emit('hide-context-menu');
      if (this.isFieldLookup(data.field) || this.isFieldHierarchy(data.field) || this.isUserField(data.field)) {
        !!this.showMultiselect
          ? this.lookupLoadingState = false
          : this.showMultiselect = true;
      } else if (!this.isFieldBoolean(data.field) 
      && !this.isFieldRecordStatus(data) 
      && !this.isFieldPostedStatus(data.field) 
      && !this.isFieldDateTime(data.field) 
      && !this.isFieldDate(data.field)) {
        this.$refs[`filterInput-${data.field.key}`].focus();
      }
      this.filterType.value = this.isFieldText(data.field) && this.filterType.value === '' ? '*' : '=';
      this.$emit('drop-down-shown');
      const filter = this.filters[data.field.key];
      this.filterType.field = data.field.key;
      this.filterText.field = data.field.key;
      if (filter) {
        this.filterType.value = filter.type;
        this.filterType.uuid = filter.uuid;
        this.filterText.value = filter.text;
        if (!this.fromDate.value) {
          this.fromDate.value = filter.fromDate;
          this.fromDate.displayValue = filter.fromDate ? this.getFormattedDate(filter.fromDate, this.getFieldDateFormat()) : null;
        }
        if (!this.toDate.value) {
          this.toDate.value = filter.toDate;
          this.toDate.displayValue = filter.toDate ? this.getFormattedDate(filter.toDate, this.getFieldDateFormat()) : null;
        }
        this.createdDate = filter.createdDate;
        this.updatedDate = filter.updatedDate;
        this.selectItems = filter.selectItems;
        this.selectedOption = filter.selected;
        this.selectedStatus = filter.selectedStatus;
        this.selectedPostedStatuses = filter.selectedPostedStatuses;
        this.field = this.data.field;
      }
    },
    onHeaderDropdownHidden() {
      this.lookupLoadingState = true;
      if (this.datePickerSubmit || (this.fromDate.open || this.toDate.open)) {
        this.$refs[`columnOptionsDropdown_${this.data.field.key}`].show();
        this.datePickerSubmit = false;
        return false;
      }
      this.$emit('drop-down-hidden');
      this.filterType.field = null;
      this.filterType.value = '';
      this.filterType.uuid = null;
      this.filterText.field = null;
      this.filterText.value = null;
      this.fromDate = {
        ...this.fromDate,
        value: null,
        displayValue: null,
        open: false,
      };
      this.fromDate.validation.isValid = true;
      this.toDate = {
        ...this.toDate,
        value: null,
        displayValue: null,
        open: false,
      };
      this.toDate.validation.isValid = true;
      this.createdDate = null;
      this.updatedDate = null;
      this.selectedOption = -1;
      this.selectedStatus = [];
      this.selectedPostedStatuses = [];
      this.field = null;
    },
    onFilter(data) {
      if (!this.toDate.validation.isValid || !this.fromDate.validation.isValid) {
        return false;
      }
      let type;
      if (this.isFieldPostedStatus(data.field)) {
        type = 'postedStatus';
      } else {
        type = data.field.type || data.field.dataTypeName || 'recordStatus';
      }
      const currentFilter = this.filters[this.filterType.field];
      const updatedFilter = {
        ...this.filters,
        [this.filterType.field]: {
          ...currentFilter,
          text: this.filterText.value || '',
          type: this.filterType.value,
          fieldType: type,
          label: data.field.label,
          fromDate: this.fromDate.value || moment(this.fromDate.displayValue, this.getFieldDateFormat()).format('YYYY-MM-DD'),
          toDate: this.toDate.value || moment(this.toDate.displayValue, this.getFieldDateFormat()).format('YYYY-MM-DD'),
          createdDate: this.createdDate,
          updatedDate: this.updatedDate,
          selected: this.selectedOption,
          selectItems: this.selectItems,
          field: this.data.field,
          selectedStatus: this.selectedStatus,
          selectedPostedStatuses: this.selectedPostedStatuses,
        },
      };
      const params = {
        updatedFilter: updatedFilter,
        query: this.buildQuery(updatedFilter),
      };

      this.$emit('filter', params);
      this.$refs['columnOptionsDropdown_' + data.field.key].hide();
    },
    sortChanged(field, sortDesc) {
      let sortBy;
      if (sortDesc === null) {
        sortBy = field.name;
      } else {
        sortBy = field.key;
      }
      let params = {};
      if (sortBy !== this.sortBy || sortDesc !== this.sortDesc) {
        this.sortBy = sortBy;
        this.sortDesc = sortDesc;
        this.contextChanged();
        params = {
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
        };
      } else {
        params = {
          sortBy: sortBy,
          sortDesc: sortDesc,
        };
      }
      this.$emit('sort-change', params);
    },
    onGroupBy(field) {
      if (!this.isGrouped) {
        this.sortBy = field.key;
      } else {
        this.sortBy = null;
      }
      this.$emit('group-by-change', {
        groupBy: field.key,
        isGrouped: !this.isGrouped,
      });
    },
    onFreeze(field) {
      this.$emit('column-freeze', field);
    },
    isDisabledOption(options, data) {
      const type = data.field.dataTypeName || data.field.type;

      if (type === 'lookup') {
        return true;
      }

      if (this.isFieldText(data.field) || this.isFieldDateTime(data.field) || type === 'date' || type === 'boolean' || type === 'parentChildHierarchy') {
        for (const i in options) {
          if (options[i].value === '<' || options[i].value === '<=' ||
              options[i].value === '>' || options[i].value === '>=') {
            return true;
          }
        }
      }
      if (type === 'wholeNumber' || type === 'decimalNumber' || type === 'floatNumber' || this.isFieldDateTime(data.field) || type === 'date' || type === 'boolean') {
        for (const i in options) {
          if (options[i].value === '*' || options[i].value === '^' ||
              options[i].value === '$' || options[i].value === '!*') {
            return true;
          }
        }
      }
      if (this.isFieldDateTime(data.field) || type === 'date' || type === 'boolean') {
        for (const i in options) {
          if (options[i].value === '=' || options[i].value === '!=') {
            return true;
          }
        }
      }
      if (data.column === 'actions' || type === 'checkbox' ||
          this.isFieldText(data.field) || type === 'number' || type === 'parentChildHierarchy') {
        for (const i in options) {
          if (options[i].value === 'from' || options[i].value === 'to') {
            return true;
          }
        }
      }
      return false;
    },
    clearFilters(data) {
      let currentFilters = {...this.filters};
      delete currentFilters[this.filterType.field];
      currentFilters = {...currentFilters};
      this.fromDate = {
        ...this.fromDate,
        value: null,
        displayValue: null,
        open: false,
      };
      this.fromDate.validation.isValid = true;
      this.toDate = {
        ...this.toDate,
        value: null,
        displayValue: null,
        open: false,
      };
      this.toDate.validation.isValid = true;
      this.selectedOption = -1;
      this.selectedStatus = [];
      this.$refs['columnOptionsDropdown_' + data.field.key].hide();
      this.contextChanged();

      const params = {
        updatedFilter: currentFilters,
        query: this.buildQuery(currentFilters),
      };

      this.$emit('filter', params);
    },
    contextChanged() {
      const context = {
        pagination: {
          perPage: this.perPage,
          page: this.currentPage,
        },
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
        filters: this.filters,
      };
      this.$emit('context-change', context);
    },
    buildQuery(filters) {
      return {id: this.$route.params.tableId, filter: filters, limit: 20, offset: 0, page: 1};
    },
    focusOnInput($event, key) {
      this.$refs[`filterInput-${key}`].focus();
    },
    isFieldNumber(field) {
      const type = field.type || field.dataTypeName;
      return type === 'wholeNumber';
    },
    isFieldNumeric(field) {
      const type = field.type || field.dataTypeName;
      return ['wholeNumber', 'decimalNumber', 'floatNumber'].includes(type);
    },
    isFieldText(field) {
      const type = field.type || field.dataTypeName;
      return ['text', 'lastUpdatedBy'].includes(type);
    },
    isFieldBoolean(field) {
      const type = field.type || field.dataTypeName;
      return type === 'boolean';
    },
    isFieldLookup(field) {
      const type = field.dataTypeName || field.type;
      return type === 'lookup';
    },
    isFieldDateTime(field) {
      const type = field.type || field.dataTypeName;
      return [this.fieldValues.dateTime, this.fieldValues.createdDate, this.fieldValues.lastUpdatedDate].includes(type);
    },
    isFieldDate(field) {
      const type = field.type || field.dataTypeName;
      return type === 'date';
    },
    isFieldHierarchy(field) {
      const type = field.type || field.dataTypeName;
      return type === 'parentChildHierarchy';
    },
    isFieldRecordStatus(data) {
      return data.column === 'recordStatus';
    },
    isFieldPostedStatus(field) {
      return field.key === 'PostedStatus';
    },
    isSelectColumn(field) {
      return field.dataTypeName === 'selectColumn';
    },
    isUserField(field) {
      return field.dataTypeName === 'user';
    },
    // Disable filter button for numeric fields when filter value is not a valid number
    isFilterButtonDisabled() {
      const isInvalidNumber = !/^-?\d+(\.\d+)?$/.test(this.filterText.value);

      if (this.isFieldNumeric(this.data.field)) {
        if (this.filterType.value === '=' || this.filterType.value === '!=') {
          return this.filterText.value ? isInvalidNumber : false;
        }
        return !this.filterText.value || isInvalidNumber;
      }

      return false;
    },
    selectAll() {
      const allItemsSelected = this.allItemsSelected;
      this.lookupOptions.forEach(item => {
        const isItemSelected = this.selectItems.find(i => i.id === item.id);
        if (!isItemSelected || allItemsSelected) {
          this.chooseItem(item);
        }
      });
    },
    fromDateDisabledDates(date) {
      return !!(this.toDate.value && date.getTime() > new Date(this.toDate.value).getTime());

    },
    toDateDisabledDates(date) {
      return !!(this.fromDate.value && date.getTime() < new Date(this.fromDate.value).getTime());
    },
  },
};
</script>

<style lang="scss">

/deep/ .mx-input-wrapper {

  .datepicker-validation {
    border-color: #dc3545;

    &:focus {
      box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
    }
  }
}

.form-control-search {
  border-radius: 0;
  z-index: 200;
  @media (max-height: 627px) {
    font-size: .8rem;
    padding: 0.300rem 0.65rem;
    height: 25px;

  }
}

.lookup-filter{
  height: 180px;
  width: 250px;
  position: relative;

  .spinner{
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .hide-lookup{
    display: none;
  }

  .multiselect__tags-wrap{
    display: none !important;
  }

  .multiselect__content-wrapper{
    max-height: 150px !important;
  }
}

.filter-list-group {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  width: 250px;
  min-height: 100px;
  max-height: 150px;
  overflow: auto;
  display: grid;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-top: none;


  .list-group-item {
    padding: 0.45rem 1.25rem;
    cursor: pointer;

    &:first-child {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }

  @media (max-height: 679px) {
    min-height: 70px;
    .list-group-item {
      padding: 0.25rem 1.05rem;

    }
  }
  @media (max-height: 627px) {
    min-height: 50px;
    .list-group-item {
      padding: 0.15rem 1.05rem;
      font-size: .8rem;

    }
  }

}


@media screen and (min-height: 400px) and (max-height: 600px) {
  /deep/ .filter-dropdown-menu {
    margin-top: 27px;
  }
}

@media screen and (min-height: 400px) and (max-height: 560px) {
  .filter-list-group {
    max-height: 32px;
  }
}

@media screen and (min-height: 560px) and (max-height: 699px) {
  .filter-list-group {
    max-height: 59px;
  }
}

@media screen and (min-height: 699px) and (max-height: 850px) {
  .filter-list-group {
    max-height: 140px;
  }
}

</style>
